import _ from 'lodash';
import { MutationTree } from 'vuex';
import { SysAdminState } from './types';
import { CognitoUser, CognitoGroup } from '@/models';
import { use } from 'chai';
import Vue from 'vue';

/**
 * Mutations
 *
 * The only way to actually change state in a Vuex store is by committing a mutation.
 * - Vuex mutations are very similar to events: each mutation has a string type and a handler.
 * - The handler function is where we perform actual state modifications - it receives the state as the first argument:
 * - You cannot directly call a mutation handler.
 * - Think of it more like event registration: "When a mutation with type X is triggered, call this handler."
 * - To invoke a mutation handler, you need to call store.commit with its type
 * - e.g. `store.commit('setCustomers', customer)`
 */
export const mutations: MutationTree<SysAdminState> = {

    /**
     * Set the user currently logged in
     *
     * @param state SysAdminState
     * @param user CognitoUser
     * @returns void
     */
    setCurrentUser(state: SysAdminState, user: CognitoUser): void {
        state.current = user;
    },

    /**
     * Set the user currently being viewed/edited
     *
     * @param state SysAdminState
     * @param user CognitoUser
     * @returns void
     */
    setSelectedUser(state: SysAdminState, user: CognitoUser): void {
        state.selecteduser = user;
    },

    /**
     * Set the users list
     *
     * @param state SysAdminState
     * @param users CognitoUser
     * @returns void
     */
    setUsers(state: SysAdminState, users: CognitoUser[]): void {
        state.list = users;
    },

    /**
     * Set the groups
     *
     * @param state SysAdminState
     * @param groups CognitoGroup
     * @returns void
     */
    setGroups(state: SysAdminState, groups: CognitoGroup[]): void {
        state.groups = groups;
    },

    /**
     * Add user
     *
     * @param state SysAdminState
     * @param user CognitoUser
     * @returns void
     */
    addUser(state: SysAdminState, user: CognitoUser): void {
        state.list.push(user);
    },

    /**
     * Set usergroups for given user
     * @param state 
     * @param sub
     * @param payload {username, groups}
     */
    setUserGroups(state: SysAdminState, payload: { username: string, usergroups: CognitoGroup[] }): void {
        let user: CognitoUser | undefined = state.list.find((u) => u.Username === payload.username);
        if (user) {
            user.CognitoUserGroups = payload.usergroups
        }
    },

    /**
     * Set usergroups for current user
     * @param state 
     * @param sub
     * @param payload groups
     */
    setSelectedUserGroups(state: SysAdminState, usergroups: CognitoGroup[]): void {
        if (state.selecteduser) {
            state.selecteduser.CognitoUserGroups = usergroups
        }
    },

    /**
     * Add usergroup for given user
     * @param state 
     * @param sub
     * @param payload {username, groupname}
     */
    addUserGroup(state: SysAdminState, payload: { username: string, groupname: string }): void {
        let group: CognitoGroup | undefined = state.groups.find((g) => g.GroupName === payload.groupname);
        if (group) {
            let userindex = state.list.findIndex((u) => u.Username === payload.username);
            if (userindex >= 0) {
                let index = state.list[userindex].CognitoUserGroups.findIndex(ug => ug.GroupName === payload.groupname);
                // Only add if not already present
                if (index < 0) {
                    state.list[userindex].CognitoUserGroups.push(group);
                }
            }
            if (state.selecteduser && state.selecteduser.Username === payload.username) {
                let index = state.selecteduser.CognitoUserGroups.findIndex(ug => ug.GroupName === payload.groupname);
                // Only add if not already present
                if (index < 0) {
                    state.selecteduser.CognitoUserGroups.push(group);
                }
            }

        }
    },

    /**
     * Remove usergroup for given user
     * @param state 
     * @param sub
     * @param payload {username, groupname}
     */
    removeUserGroup(state: SysAdminState, payload: { username: string, groupname: string }): void {
        let userindex = state.list.findIndex((u) => u.Username === payload.username);
        let groupindex = state.groups.findIndex((g: CognitoGroup) => g.GroupName === payload.groupname);
        if (groupindex >= 0) {
            state.groups[groupindex] = new CognitoGroup(state.groups[groupindex]);
            if (userindex >= 0) {
                let index = state.list[userindex].CognitoUserGroups.findIndex(ug => ug.GroupName === payload.groupname);
                if (index >= 0) {
                    state.list[userindex].CognitoUserGroups.splice(index, 1);
                }
            }
            if (state.selecteduser && state.selecteduser.Username === payload.username) {
                let index = state.selecteduser.CognitoUserGroups.findIndex(ug => ug.GroupName === payload.groupname);
                // Only add if not already present
                if (index >= 0) {
                    state.selecteduser.CognitoUserGroups.splice(index, 1);
                }
            }
        }
    },


    /**
     * Set custom attribute for given user
     * @param state 
     * @param sub
     * @param attribute_name
     * @param attribute_value 
     */
    setUserSttribute(state: SysAdminState, payload: { username: string, attributename: string, attributevalue: string }): void {
        let user: CognitoUser | undefined = state.list.find((u) => u.Username === payload.username);
        if (user) {
            user.setAttributeValue(payload.attributename, payload.attributevalue);
        }
    },

    /**
     * ++ Unsaved changes
     * 
     * @param state SysAdminState
     * @param element string
     * @returns void
     */
    addUnsavedChange(state: SysAdminState, element: string): void {
        state.updates = [...new Set(state.updates.concat([element]))];
    },

    /**
     * Reset Unsaved changes
     * 
     * @param state SysAdminState
     * @returns void
     */
    resetUnsavedChanges(state: SysAdminState): void {
        state.updates = [];
    },

    resetState(state: SysAdminState): void {
        state.list = [];
        state.updates = [],
        state.current = new CognitoUser;
        state.groups = [];
    },

		rehydrateState(state: SysAdminState): void {
			state.list = state.list.map((x: any) => new CognitoUser(x));
			state.current = new CognitoUser(state.current);
			state.groups = state.groups.map((x: any) => new CognitoGroup(x));
		}

};
